import { useEffect, useState } from 'react';
import { slackHuddleAPI } from '../services/api';

/**
 * "Connect Rithmo to Slack (Huddles)" — org-admin only.
 *
 * Guided login-capture: the admin submits the Rithmo huddle account's workspace login;
 * the backend drives a real Slack login, captures the member session (xoxc + d cookie),
 * and stores it encrypted so Rithmo can auto-join huddles. Renders in Settings → Integrations.
 */
export default function SlackHuddleConnect() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ host: '', email: '', password: '', totp: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);

  const refresh = async () => {
    try {
      const res = await slackHuddleAPI.getStatus();
      setStatus(res?.data ?? res);
    } catch (e) {
      setError(e?.message || 'Failed to load status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  const onConnect = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await slackHuddleAPI.connect({
        host: form.host.trim(),
        email: form.email.trim(),
        password: form.password,
        totp: form.totp.trim() || undefined,
      });
      setForm({ host: '', email: '', password: '', totp: '' });
      setShowForm(false);
      await refresh();
    } catch (e2) {
      setError(e2?.message || 'Connect failed');
    } finally {
      setSubmitting(false);
    }
  };

  const onDisconnect = async () => {
    setError('');
    try {
      await slackHuddleAPI.disconnect();
      await refresh();
    } catch (e) {
      setError(e?.message || 'Disconnect failed');
    }
  };

  if (loading) return <div className="text-sm text-gray-400">Loading Slack Huddles…</div>;

  const connected = Boolean(status?.connected);
  const isAdmin = Boolean(status?.isOrgAdmin);

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-base font-semibold text-gray-900">Slack Huddles</h3>
          <p className="mt-1 text-sm text-gray-500">
            Let Rithmo auto-join huddles in your allow-listed channels and capture them as meetings —
            validating completed commitments and capturing new ones.
          </p>
        </div>
        <span
          className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-medium ${
            connected ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
          }`}
        >
          {connected ? 'Connected' : 'Not connected'}
        </span>
      </div>

      {!isAdmin && (
        <p className="mt-4 text-sm text-amber-600">Only an org admin can connect huddle capture.</p>
      )}

      {error && <p className="mt-4 text-sm text-red-600">{error}</p>}

      {connected ? (
        <div className="mt-4 flex items-center justify-between">
          <p className="text-sm text-gray-600">
            Connected as member <code className="rounded bg-gray-100 px-1">{status?.slackMemberUserId || '—'}</code>
            {status?.status ? ` · ${status.status}` : ''}
          </p>
          {isAdmin && (
            <button
              onClick={onDisconnect}
              className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
            >
              Disconnect
            </button>
          )}
        </div>
      ) : (
        isAdmin && (
          <div className="mt-4">
            {!showForm ? (
              <button
                onClick={() => setShowForm(true)}
                className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
              >
                Connect Rithmo to Slack (Huddles)
              </button>
            ) : (
              <form onSubmit={onConnect} className="space-y-3">
                <p className="text-xs text-gray-500">
                  Enter the login for the Slack member that should join huddles (a dedicated Rithmo account
                  is recommended). Rithmo signs in once and stores the session encrypted.
                </p>
                <input
                  required
                  placeholder="Workspace host (e.g. acme.slack.com)"
                  value={form.host}
                  onChange={(e) => setForm({ ...form, host: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
                <input
                  required
                  type="email"
                  placeholder="Account email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
                <input
                  required
                  type="password"
                  placeholder="Account password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
                <input
                  placeholder="2FA code (if enabled)"
                  value={form.totp}
                  onChange={(e) => setForm({ ...form, totp: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
                <div className="flex items-center gap-2">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-60"
                  >
                    {submitting ? 'Connecting…' : 'Connect'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </div>
        )
      )}
    </div>
  );
}
